// Pustaka anime: menyusun isi folder anime (default /ANIME) menjadi daftar
// seri -> season -> episode untuk halaman streaming (AnimePage).
//
// Struktur yang dikenali:
//   /ANIME/<Seri>/<Season>/<episode>.mkv   (season = folder mana pun di bawah seri yang berisi video)
//   /ANIME/<Seri>/<film atau OVA>.mkv      (video langsung di folder seri -> grup "Movie", "OVA", ... sesuai isinya)
//   /ANIME/<Seri>/<episode>.mkv            (seri tanpa subfolder -> satu grup "Episode")
import { FILE_DELIMITER, fileKeyOf } from './disbox-file-manager';
import { getAccount } from './api.js';
import { isVideoFile } from './file-utils';
import { getProgress } from './watch-progress';
import { matchesLink } from './link-id';

// Hanya akun ini yang melihat menu & halaman Anime.
export const ANIME_ACCOUNTS = ['admin'];
const DEFAULT_ROOT_NAMES = ['anime'];
const ROOT_STORAGE_KEY = 'disbox_anime_root';

export function canUseAnime() {
    const account = getAccount();
    return Boolean(account && ANIME_ACCOUNTS.includes(String(account.username || '').toLowerCase()));
}

const naturalCompare = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

// Folder akar anime: pilihan tersimpan, atau folder teratas bernama "Anime".
export function findAnimeRoot(fileManager) {
    let saved = null;
    try {
        saved = localStorage.getItem(ROOT_STORAGE_KEY);
    } catch {
        // localStorage tidak tersedia: pakai deteksi otomatis.
    }
    if (saved) {
        const node = fileManager.getFile(saved, false);
        if (node && node.type === 'directory') return saved;
    }
    const top = Object.values(fileManager.getChildren(''));
    const match = top.find((child) => child.type === 'directory' && DEFAULT_ROOT_NAMES.includes(child.name.toLowerCase()));
    return match ? match.path : null;
}

export function setAnimeRoot(path) {
    try {
        if (path) localStorage.setItem(ROOT_STORAGE_KEY, path);
        else localStorage.removeItem(ROOT_STORAGE_KEY);
    } catch {
        // Abaikan: pilihan hanya berlaku sampai halaman ditutup.
    }
}

// ---------- nama & nomor episode ----------

const EXTRA_PATTERN = /\b(NC ?OP|NC ?ED|creditless|PV|CM|trailer|preview|menu)\b/i;
const MOVIE_PATTERN = /\b(movie|film|the movie|gekijouban)\b/i;
const OVA_PATTERN = /\b(OVA|OAD|ONA|special|spesial|SP)\b/i;
// OVA/OAD = episode tambahan: ditulis "OVA" walaupun nama filenya bernomor
// (misal "BD - 13 OVA", "OAD - 13"), dan tidak dihitung sebagai episode.
// Samakan dengan episodeLabel di discord-bot/anime-preview.js.
const OVA_TAG = /\b(OVA|OAD)\b/i;

function stripNoise(base) {
    return base
        .replace(/\[[^\]]*\]|\([^)]*\)|【[^】]*】/g, ' ')
        .replace(/\b\d{3,4}p\b|\b[xh]\.?26[45]\b|\bHEVC\b|\bAVC\b|\b(?:8|10)[- ]?bit\b|\bFLAC\b|\bAAC\b|\bBD(?:rip)?\b|\bWEB(?:-?DL|rip)?\b/gi, ' ')
        .replace(/\bS\d{1,2}\b/gi, ' ')
        .replace(/\bv\d\b/gi, ' ');
}

// { number, isFinal, kind: 'episode' | 'movie' | 'ova' | 'extra', tag?: 'OVA' | 'Spesial' }
export function parseEpisodeName(name) {
    const base = name.replace(/\.[^.]+$/, '');
    const cleaned = stripNoise(base);
    const isFinal = /\b(END|FINAL|TAMAT)\b/i.test(base);
    let match = cleaned.match(/(?:^|[\s_.-])(?:ep|eps|episode|e)[\s._-]?(\d{1,4})(?!\d)/i)
        || cleaned.match(/\s-\s+(\d{1,4})(?!\d)/);
    if (!match) {
        const all = [...cleaned.matchAll(/(?:^|[\s_.-])(\d{1,4})(?=[\s_.-]|$)/g)];
        match = all.length ? all[all.length - 1] : null;
    }
    const number = match ? Number(match[1]) : null;
    if (EXTRA_PATTERN.test(base) && number === null) return { number: null, isFinal: false, kind: 'extra' };
    if (EXTRA_PATTERN.test(base)) return { number, isFinal: false, kind: 'extra' };
    if (OVA_TAG.test(base)) return { number, isFinal: false, kind: 'ova', tag: 'OVA' };
    if (number === null && MOVIE_PATTERN.test(base)) return { number: null, isFinal: false, kind: 'movie' };
    if (number === null && OVA_PATTERN.test(base)) return { number: null, isFinal: false, kind: 'ova', tag: 'Spesial' };
    return number === null ? { number, isFinal, kind: 'ova', tag: 'Spesial' } : { number, isFinal, kind: 'episode' };
}

export function episodeTitle(episode) {
    if (episode.kind === 'movie') return 'Movie';
    if (episode.kind === 'extra') return /NC ?OP|creditless op/i.test(episode.name) ? 'Opening (tanpa kredit)' : /NC ?ED/i.test(episode.name) ? 'Ending (tanpa kredit)' : 'Ekstra';
    if (episode.kind === 'ova') return `${episode.tag || 'Spesial'}${episode.tagIndex ? ` ${episode.tagIndex}` : ''}`;
    return `Episode ${episode.number}${episode.isFinal ? ' (Final)' : ''}`;
}

// ---------- judul seri / season ----------

export function cleanSeriesName(name) {
    const cleaned = name
        .replace(/\[[^\]]*\]|\([^)]*\)/g, ' ')
        .replace(/\b(anime|all|batch|complete|komplit|collection|koleksi)\b/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return cleaned || name;
}

function isSpecialGroupName(name) {
    return /\b(special|spesial|ova|oad|movie|film|extra|ekstra|sp)\b/i.test(name);
}

// Label season: tanpa tag [..] dan tanpa awalan yang sama di semua season.
function seasonLabels(names) {
    const stripped = names.map((n) => n.replace(/\[[^\]]*\]/g, ' ').replace(/\s+/g, ' ').trim() || n);
    if (stripped.length < 2) return stripped;
    const words = stripped.map((n) => n.split(' '));
    const sharedAt = (i) => words.every((w) => w.length > i + 1 && w[i].toLowerCase() === words[0][i].toLowerCase());
    let common = 0;
    while (sharedAt(common)) common += 1;
    return stripped.map((n, i) => (common > 0 ? words[i].slice(common).join(' ') : n));
}

// ---------- membangun pustaka ----------

function timeOf(node) {
    const time = Date.parse(node.created_at || node.updated_at || '');
    return Number.isFinite(time) ? time : 0;
}

function videoChildren(fileManager, dirPath) {
    return Object.values(fileManager.getChildren(dirPath))
        .filter((child) => child.type !== 'directory' && child.content && isVideoFile(child.name));
}

// folderName: nama folder season. Folder "OVA"/"OAD" -> isinya OVA; folder
// "Movie"/"Film" -> video tanpa nomor di dalamnya movie (yang bernomor tetap
// episode, misal season TV yang foldernya salah nama).
function buildEpisodes(files, folderName = '') {
    const ovaFolder = OVA_TAG.test(folderName);
    const movieFolder = MOVIE_PATTERN.test(folderName);
    const episodes = files.map((file) => {
        const parsed = parseEpisodeName(file.name);
        if (parsed.kind !== 'extra' && ovaFolder) Object.assign(parsed, { kind: 'ova', tag: 'OVA', isFinal: false });
        else if (movieFolder && parsed.number === null && parsed.kind === 'ova' && parsed.tag !== 'OVA') Object.assign(parsed, { kind: 'movie', tag: undefined });
        return { ...parsed, name: file.name, path: file.path, size: file.size, node: file };
    });
    const rank = { episode: 0, ova: 1, movie: 2, extra: 3 };
    episodes.sort((a, b) => (rank[a.kind] - rank[b.kind])
        || ((a.number ?? 1e9) - (b.number ?? 1e9))
        || naturalCompare(a.name, b.name));
    // Lebih dari satu OVA/spesial di grup yang sama: "OVA 1", "OVA 2", ...
    for (const tag of ['OVA', 'Spesial']) {
        const group = episodes.filter((e) => e.kind === 'ova' && e.tag === tag);
        if (group.length > 1) group.forEach((e, i) => { e.tagIndex = i + 1; });
    }
    return episodes;
}

// Nama grup dari isinya: "Movie", "OVA", "Movie & OVA", ... (dulu selalu "Film & Spesial").
function groupLabel(episodes) {
    const names = { movie: 'Movie', extra: 'Ekstra', episode: 'Episode' };
    const labels = [...new Set(episodes.map((e) => (e.kind === 'ova' ? e.tag || 'Spesial' : names[e.kind])))];
    return labels.join(' & ') || 'Episode';
}

// Seri yang isinya cuma satu video (Jujutsu Kaisen 0, film dari kontributor)
// adalah movie, walaupun nama filenya tidak menyebut "movie".
function markSingleAsMovie(episodes) {
    const only = episodes.length === 1 ? episodes[0] : null;
    if (only && (only.kind === 'episode' || (only.kind === 'ova' && only.tag !== 'OVA'))) {
        Object.assign(only, { kind: 'movie', number: null, isFinal: false, tag: undefined });
    }
}

function collectSeasonDirs(fileManager, dirPath, out) {
    for (const child of Object.values(fileManager.getChildren(dirPath))) {
        if (child.type !== 'directory') continue;
        const videos = videoChildren(fileManager, child.path);
        if (videos.length > 0) out.push({ path: child.path, name: child.name, files: videos });
        collectSeasonDirs(fileManager, child.path, out);
    }
}

function buildSeries(fileManager, dir) {
    const dirs = [];
    collectSeasonDirs(fileManager, dir.path, dirs);
    const rootVideos = videoChildren(fileManager, dir.path);
    // Urutan: folder "Season N" dulu, lalu folder lain (spin-off, rentang episode), lalu spesial/film.
    const rank = (name) => (isSpecialGroupName(name) ? 2 : /\b(season|musim|s\d{1,2})\b/i.test(name) ? 0 : 1);
    dirs.sort((a, b) => (rank(a.name) - rank(b.name)) || naturalCompare(a.path, b.path));
    const labels = seasonLabels(dirs.map((d) => d.path.slice(dir.path.length + 1).replace(/\//g, ' / ')));
    const seasons = dirs.map((d, i) => ({ id: d.path, path: d.path, label: labels[i], episodes: buildEpisodes(d.files, d.name) }));
    if (rootVideos.length > 0) {
        const episodes = buildEpisodes(rootVideos);
        if (seasons.length === 0) markSingleAsMovie(episodes);
        seasons.push({ id: dir.path, path: dir.path, label: groupLabel(episodes), episodes });
    }
    if (seasons.length === 0) return null;
    const allEpisodes = seasons.flatMap((s) => s.episodes.map((e) => ({ ...e, seasonId: s.id, seasonLabel: s.label })));
    const updatedAt = Math.max(0, ...allEpisodes.map((e) => timeOf(e.node)));
    return {
        id: dir.path,
        path: dir.path,
        folderName: dir.name,
        title: cleanSeriesName(dir.name),
        seasons,
        episodes: allEpisodes,
        episodeCount: allEpisodes.filter((e) => e.kind === 'episode').length,
        updatedAt,
    };
}

export function buildLibrary(fileManager, rootPath) {
    if (!rootPath) return [];
    const series = [];
    for (const child of Object.values(fileManager.getChildren(rootPath))) {
        if (child.type !== 'directory') continue;
        const item = buildSeries(fileManager, child);
        if (item) series.push(item);
    }
    // Video lepas langsung di folder anime: tiap file jadi "seri" sendiri (film).
    for (const file of videoChildren(fileManager, rootPath)) {
        const episodes = buildEpisodes([file]);
        markSingleAsMovie(episodes);
        const label = groupLabel(episodes);
        series.push({
            id: file.path,
            path: file.path,
            folderName: file.name.replace(/\.[^.]+$/, ''),
            title: cleanSeriesName(file.name.replace(/\.[^.]+$/, '')),
            seasons: [{ id: file.path, path: file.path, label, episodes }],
            episodes: episodes.map((e) => ({ ...e, seasonId: file.path, seasonLabel: label })),
            episodeCount: 0,
            updatedAt: timeOf(file),
        });
    }
    series.sort((a, b) => naturalCompare(a.title, b.title));
    return series;
}

// ---------- progres tontonan ----------

export function episodeProgress(episode) {
    const saved = getProgress(fileKeyOf(episode.node) || `p:${episode.path}`);
    if (!saved) return { percent: 0, watched: false, position: 0, updatedAt: 0 };
    const watched = Boolean(saved.finished) || (saved.duration > 0 && saved.position >= saved.duration - 30);
    const percent = watched ? 100 : saved.duration ? Math.min(100, (saved.position / saved.duration) * 100) : 0;
    return { percent, watched, position: saved.position || 0, updatedAt: saved.updatedAt || 0 };
}

// Episode yang sebaiknya diputar berikutnya: lanjutan yang belum selesai,
// atau episode sesudah yang terakhir ditonton, atau episode pertama.
export function nextUpEpisode(series) {
    let last = null;
    series.episodes.forEach((episode, index) => {
        const progress = episodeProgress(episode);
        if (progress.updatedAt && (!last || progress.updatedAt > last.progress.updatedAt)) last = { index, progress };
    });
    if (!last) return { episode: series.episodes[0], resume: false };
    if (!last.progress.watched) return { episode: series.episodes[last.index], resume: true };
    const next = series.episodes.slice(last.index + 1).find((e) => e.kind === 'episode') || series.episodes[last.index + 1];
    return next ? { episode: next, resume: false } : { episode: series.episodes[last.index], resume: false, finished: true };
}

export function lastWatchedAt(series) {
    return Math.max(0, ...series.episodes.map((e) => episodeProgress(e).updatedAt));
}

// path boleh path asli atau kode link (lihat link-id.js).
export function findEpisode(library, path) {
    for (const series of library) {
        const index = series.episodes.findIndex((e) => matchesLink(path, e.path));
        if (index >= 0) return { series, episode: series.episodes[index], index };
    }
    return null;
}

export function parentPath(path) {
    return path.substring(0, path.lastIndexOf(FILE_DELIMITER));
}
